import * as React from "react";
import { useState, useEffect } from "react";
import { 
  X,
  FolderPlus
} from "lucide-react";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  createFolder
} from "../../services/documentUploadService";
import { KnowledgeBaseWithDocuments } from "../../../../types/navigationTypes";
import { cn } from "@/lib/utils";

interface CategoryCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  knowledgeBaseId?: string;
  knowledgeBases: KnowledgeBaseWithDocuments[];
  onFolderCreated: () => void;
}

const CategoryCreateModal: React.FC<CategoryCreateModalProps> = ({
  isOpen,
  onClose,
  knowledgeBaseId: initialKnowledgeBaseId,
  knowledgeBases,
  onFolderCreated
}) => {
  const [selectedKnowledgeBaseId, setSelectedKnowledgeBaseId] = useState<string>('');
  const [folderName, setFolderName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      resetForm();
    } else {
      const kbToUse = initialKnowledgeBaseId || knowledgeBases[0]?.id || '';
      setSelectedKnowledgeBaseId(kbToUse);
    }
  }, [isOpen, initialKnowledgeBaseId, knowledgeBases]);

  const resetForm = () => {
    setSelectedKnowledgeBaseId('');
    setFolderName('');
    setDescription('');
    setError(null);
  };

  const handleCreate = async () => {
    if (!folderName.trim()) {
      setError('Enter a name for the folder');
      return;
    }

    if (!selectedKnowledgeBaseId) {
      setError('Select a knowledge base');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await createFolder(
        selectedKnowledgeBaseId,
        folderName.trim(),
        description.trim() || undefined
      );

      resetForm();
      onFolderCreated();
      onClose();
    } catch (err) {
      console.error('[CategoryCreateModal] Error creating folder:', err);
      setError(err instanceof Error ? err.message : 'Unknown error creating folder');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-[450px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderPlus className="h-5 w-5" />
            Create Folder
          </DialogTitle>
          <DialogDescription>
            Add a new folder to organize your documents
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {knowledgeBases.length > 1 && (
            <div className="space-y-2">
              <Label htmlFor="kb-select">Knowledge Base</Label>
              <select
                id="kb-select"
                value={selectedKnowledgeBaseId}
                onChange={(e) => setSelectedKnowledgeBaseId(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                disabled={loading}
              >
                {knowledgeBases.map(kb => (
                  <option key={kb.id} value={kb.id}>
                    {kb.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {knowledgeBases.length === 1 && (
            <div className="text-sm text-muted-foreground">
              Creating folder in: <span className="font-medium text-foreground">{knowledgeBases[0].name}</span>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="folder-name">
              Folder Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="folder-name"
              value={folderName}
              onChange={(e) => setFolderName(e.target.value)}
              placeholder="e.g., Contracts, Reports, Templates..."
              disabled={loading}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleCreate();
                }
              }}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="folder-description">Description (optional)</Label>
            <Input
              id="folder-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of this folder..."
              disabled={loading}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={loading || !folderName.trim() || !selectedKnowledgeBaseId}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <FolderPlus className="mr-2 h-4 w-4" />
                Create Folder
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CategoryCreateModal;
